import React, { useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Box, Grid, TextField, Button, Typography, FormHelperText } from "@mui/material";
import "./MyEditor.css";
import { createBlog } from "../../services";

interface ContentState {
  title: string;
  coverImageSrc: string;
  author?: string;
  content: string;
  summary: string;
  tags: string[];
  categories: string[];
  publishedAt: Date;
}

const MyEditor: React.FC = () => {
  const [content, setContent] = useState<ContentState>({
    title: "",
    coverImageSrc: "",
    content: "",
    summary: "",
    tags: [],
    categories: [],
    publishedAt: new Date(),
  });

  const [loading, setLoading] = useState<boolean>(false);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    title: "",
    coverImageSrc: "",
    content: "",
    summary: "",
  });

  const handleSubmit = async () => {
    // Validation for required fields
    let errors = {
      title: "",
      coverImageSrc: "",
      content: "",
      summary: "",
    };

    if (!content.title.trim()) {
      errors.title = "Title is required!";
    }
    if (!content.coverImageSrc.trim()) {
      errors.coverImageSrc = "Cover Image URL is required!";
    }
    if (!content.content.trim()) {
      errors.content = "Content is required!";
    }
    if (!content.summary.trim()) {
      errors.summary = "Summary is required!";
    }

    // If there are any validation errors, update the state and return early
    if (Object.values(errors).some((error) => error !== "")) {
      setValidationErrors(errors);
      return;
    }

    // Clear previous errors if validation passed
    setValidationErrors({
      title: "",
      coverImageSrc: "",
      content: "",
      summary: "",
    });

    setLoading(true);

    try {
      const response = await createBlog(content);
      // console.log(response);
      if (response) {
        alert("Content saved successfully!");
        setContent({
          title: "",
          coverImageSrc: "",
          content: "",
          summary: "",
          tags: [],
          categories: [],
          publishedAt: new Date(),
        });
      } else {
        alert("Failed to save content. Please try again.");
      }
    } catch (error) {
      console.error("Error saving content:", error);
      alert("Failed to save content. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography variant="h4" className="heading" gutterBottom>
        Editor
      </Typography>

      <Grid container spacing={3}>
        {/* Left Side */}
        <Grid item xs={12} lg={6}>
          <TextField
            fullWidth
            label="Title"
            variant="outlined"
            value={content.title}
            onChange={(e) => setContent({ ...content, title: e.target.value })}
            margin="normal"
            required
            type="text"
            error={!!validationErrors.title}
            helperText={validationErrors.title}
          />
          <TextField
            fullWidth
            label="Cover Image URL"
            variant="outlined"
            value={content.coverImageSrc}
            onChange={(e) =>
              setContent({ ...content, coverImageSrc: e.target.value })
            }
            margin="normal"
            required
            error={!!validationErrors.coverImageSrc}
            helperText={validationErrors.coverImageSrc}
          />
          <TextField
            fullWidth
            label="Summary"
            multiline
            rows={3}
            variant="outlined"
            value={content.summary}
            onChange={(e) =>
              setContent({ ...content, summary: e.target.value })
            }
            margin="normal"
            required
            error={!!validationErrors.summary}
            helperText={validationErrors.summary}
          />
          <CKEditor
            editor={ClassicEditor}
            data={content.content}
            onChange={(event, editor) => {
              const data = editor.getData();
              setContent({ ...content, content: data });
            }}
            config={{
              toolbar: [
                "heading",
                "|",
                "bold",
                "italic",
                "link",
                "bulletedList",
                "numberedList",
                "undo",
                "redo",
              ],
            }}
          />
          {validationErrors.content && (
            <FormHelperText error sx={{ marginBottom: 2 }}>
              {validationErrors.content}
            </FormHelperText>
          )}
          <TextField
            fullWidth
            label="Tags (comma-separated)"
            variant="outlined"
            value={content.tags.join(", ")}
            onChange={(e) =>
              setContent({
                ...content,
                tags: e.target.value.split(",").map((tag) => tag.trim()),
              })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Categories (comma-separated)"
            variant="outlined"
            value={content.categories.join(", ")}
            onChange={(e) =>
              setContent({
                ...content,
                categories: e.target.value.split(",").map((cat) => cat.trim()),
              })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            type="date"
            label="Published At"
            variant="outlined"
            value={content.publishedAt.toISOString().slice(0, 10)}
            onChange={(e) =>
              setContent({
                ...content,
                publishedAt: new Date(e.target.value),
              })
            }
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? "Saving..." : "Save Content"}
          </Button>
        </Grid>

        {/* Right Side */}
        <Grid item xs={12} lg={6}>
          <Box className="preview-container">
            <Typography variant="h5" className="preview-heading">
              Content Preview
            </Typography>
            <div className="preview preview-content">
              <h4>Title:</h4>
              <p>{content.title}</p>
              <h4>Cover Image:</h4>
              {content.coverImageSrc ? (
                <img src={content.coverImageSrc} alt="Cover" />
              ) : (
                <p>No cover image added.</p>
              )}
              <h4>Content:</h4>
              <div dangerouslySetInnerHTML={{ __html: content.content }} />
              <h4>Summary:</h4>
              <p>{content.summary}</p>
              <h4>Tags:</h4>
              <p>{content.tags.length ? content.tags.join(', ') : 'No tags added.'}</p>
              <h4>Categories:</h4>
              <p>{content.categories.length ? content.categories.join(', ') : 'No categories added.'}</p>
              <h4>Published At:</h4>
              <p>{content.publishedAt.toLocaleString()}</p>
            </div>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyEditor;
